// src/frontend/src/components/Pricing/PricingContent.tsx - Complete file with trial user fixes
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';

interface PricingContentProps {
  showFullFAQ?: boolean;
  showBillingToggle?: boolean;
  sectionId?: string;
  forcePayment?: boolean; // New prop to force payment flow
}

interface PriceData {
  key: string;
  priceId: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year' | 'one_time';
  intervalCount: number;
  product: {
    name: string;
    description: string;
  };
  error?: string;
}

interface LaunchDiscount {
  id: string;
  name: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: 'forever' | 'once' | 'repeating';
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  valid: boolean;
}

interface PricingData {
  plans: {
    solo_core: {
      monthly: PriceData;
      yearly: PriceData;
    };
    solo_pro: {
      monthly: PriceData;
      yearly: PriceData;
    };
    team_pilot: PriceData;
  };
  discounts: {
    launch: LaunchDiscount | null;
  };
  features: {
    solo_core: string[];
    solo_pro: string[];
    team_pilot: string[];
  };
}

const PricingContent: React.FC<PricingContentProps> = ({ 
  showFullFAQ = true, 
  showBillingToggle = true,
  sectionId,
  forcePayment = false 
}) => {
  const [isYearly, setIsYearly] = useState(false);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [usingFallback, setUsingFallback] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [userPlan, setUserPlan] = useState<any>(null);
  
  // Check if user is coming from expired trial
  const isTrialExpired = searchParams.get('trial_expired') === 'true';
  const fromExpiration = searchParams.get('from') === 'expiration';
  const fromTrialLimit = searchParams.get('from') === 'trial_limit';
  
  // Determine if we should show payment flow instead of trial
  const shouldShowPayment = forcePayment || isTrialExpired || fromExpiration || fromTrialLimit ||
    (userPlan && !userPlan.isTrialActive) ||
    (userPlan && userPlan.isTrialActive && userPlan.trialDaysLeft <= 0) ||
    (isAuthenticated && userPlan); // Allow upgrades during active trial

  // Fallback pricing data (your current hardcoded prices)
  const getDefaultPricingData = (): PricingData => ({
    plans: {
      solo_core: {
        monthly: { 
          key: 'solo_core_monthly', 
          priceId: 'price_fallback_solo_core_monthly', 
          amount: 19.99, 
          currency: 'usd', 
          interval: 'month', 
          intervalCount: 1, 
          product: { name: 'Solo Core', description: 'PMP students, hobby PMs' } 
        },
        yearly: { 
          key: 'solo_core_yearly', 
          priceId: 'price_fallback_solo_core_yearly', 
          amount: 199.92, 
          currency: 'usd', 
          interval: 'year', 
          intervalCount: 1, 
          product: { name: 'Solo Core', description: 'PMP students, hobby PMs' } 
        }
      },
      solo_pro: {
        monthly: { 
          key: 'solo_pro_monthly', 
          priceId: 'price_fallback_solo_pro_monthly', 
          amount: 39.99, 
          currency: 'usd', 
          interval: 'month', 
          intervalCount: 1, 
          product: { name: 'Solo Pro', description: 'Mid-career PMs seeking promotion' } 
        },
        yearly: { 
          key: 'solo_pro_yearly', 
          priceId: 'price_fallback_solo_pro_yearly', 
          amount: 399.92, 
          currency: 'usd', 
          interval: 'year', 
          intervalCount: 1, 
          product: { name: 'Solo Pro', description: 'Mid-career PMs seeking promotion' } 
        }
      },
      team_pilot: { 
        key: 'team_pilot', 
        priceId: 'price_fallback_team_pilot', 
        amount: 956, 
        currency: 'usd', 
        interval: 'one_time', 
        intervalCount: 1, 
        product: { name: 'Team Pilot-10', description: 'Small PMOs or L&D leads' } 
      }
    },
    discounts: {
      launch: { 
        id: 'launch20', 
        name: 'Launch Discount', 
        percentOff: 20, 
        amountOff: null, 
        currency: null, 
        duration: 'forever', 
        durationInMonths: null, 
        maxRedemptions: null, 
        timesRedeemed: 0, 
        valid: true 
      }
    },
    features: {
      solo_core: [
        '20 coaching sessions / month',
        'All persona packs',
        'PDF feedback report',
        'Email support'
      ],
      solo_pro: [
        '60 coaching sessions / month',
        'Real-time coaching tip after every message',
        'Custom scenario creator',
        'Priority email support',
        'Everything in Solo Core'
      ],
      team_pilot: [
        '10 seats, 60 days',
        '300 coaching sessions per seat',
        'Team dashboard',
        'Private leaderboard',
        'Roll-up PDF report',
        'Dedicated support'
      ]
    }
  });

  const faqItems = [
    {
      question: "How is this different from other coaching platforms?",
      answer: "Unlike generic coaching platforms like BetterUp or CoachHub that focus on general leadership skills, PM Coach is laser-focused on the specific conversations and challenges that project managers face daily. Every scenario, stakeholder persona, and piece of feedback is designed specifically for PM skill development."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. If you cancel, you'll continue to have access until the end of your current billing period."
    },
    {
      question: "What happens after my free trial?",
      answer: "Your 14-day free trial gives you full access to all Solo Core features. After the trial, you can choose to subscribe to continue using the platform, or your account will be paused until you decide to subscribe."
    },
    {
      question: "How do the coaching sessions work?",
      answer: "Each coaching session is an AI-powered conversation with a realistic stakeholder persona. You'll receive real-time feedback on your communication approach, and detailed performance analysis after each session to help you improve."
    },
    {
      question: "Is there a team discount for larger organizations?",
      answer: "Yes! For organizations needing more than 10 seats, we offer custom Enterprise plans with volume discounts, SSO integration, and dedicated support. Contact us for a personalized quote."
    },
    {
      question: "What if I need more coaching sessions per month?",
      answer: "You can upgrade to Solo Pro for 3x more sessions, or if you consistently need more, contact us about custom Enterprise pricing that can include unlimited sessions."
    }
  ];

  useEffect(() => {
    fetchPricingData();
    if (isAuthenticated) {
      fetchUserPlan();
    }
  }, [isAuthenticated]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const fetchPricingData = async () => {
    try {
      setIsLoading(true);
      setError('');
      setUsingFallback(false);
      
      const response = await api.get('/payments/pricing');
      setPricingData(response.data.data);
      console.log('✅ Dynamic pricing loaded from Stripe');
    } catch (err: any) {
      console.warn('⚠️ Failed to fetch dynamic pricing, using fallback:', err.message);
      setError('Using offline pricing');
      setUsingFallback(true);
      setPricingData(getDefaultPricingData());
    } finally {
      setIsLoading(false);
    }
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      // Redirect to Stripe Checkout
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Helper functions
  const formatPrice = (amount: number, currency: string = 'usd'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const calculateDiscountedPrice = (originalPrice: number, discount: LaunchDiscount | null): number => {
    if (!discount || !discount.valid) return originalPrice;

    let discounted = originalPrice;

    if (discount.percentOff) {
      discounted = originalPrice * (1 - discount.percentOff / 100);
    } else if (discount.amountOff) {
      discounted = Math.max(0, originalPrice - discount.amountOff);
    }

    // Round to nearest .99
    return Math.floor(discounted) + 0.99;
  };

  const calculateAnnualSavings = (monthlyPrice: number, yearlyPrice: number): number => {
    const monthlyTotal = monthlyPrice * 12;
    return Math.max(0, monthlyTotal - yearlyPrice);
  };

  const getPrice = (priceData: PriceData | undefined): string => {
    if (!priceData || priceData.error) return 'N/A';
    
    let price = priceData.amount;
    
    // Apply launch discount if available
    if (pricingData?.discounts.launch) {
      price = calculateDiscountedPrice(price, pricingData.discounts.launch);
    }
    
    return formatPrice(price);
  };

  const getOriginalPrice = (priceData: PriceData | undefined): string => {
    if (!priceData || priceData.error) return 'N/A';
    return formatPrice(priceData.amount);
  };

  const hasDiscount = (): boolean => {
    return !!(pricingData?.discounts.launch?.valid);
  };

  const getDiscountPercentage = (): number => {
    return pricingData?.discounts.launch?.percentOff || 20;
  };

  const getSavings = (planKey: 'solo_core' | 'solo_pro'): number => {
    if (!pricingData) return 0;
    
    let monthlyPrice = pricingData.plans[planKey].monthly.amount;
    let yearlyPrice = pricingData.plans[planKey].yearly.amount;
    
    // Apply discount to both if available
    if (pricingData.discounts.launch?.valid) {
      monthlyPrice = calculateDiscountedPrice(monthlyPrice, pricingData.discounts.launch);
      yearlyPrice = calculateDiscountedPrice(yearlyPrice, pricingData.discounts.launch);
    }
    
    return calculateAnnualSavings(monthlyPrice, yearlyPrice);
  };

  // FIXED: Button text logic for trial users
  const getButtonText = (planKey: string) => {
    if (planKey === 'team_pilot') {
      return 'Get Team Pilot';
    }

    if (!isAuthenticated) {
      return 'Start 14-Day Free Trial';
    }

    // FIXED: Handle trial users properly - allow them to subscribe to Solo Core
    if (isAuthenticated && userPlan && userPlan.isTrialActive) {
      if (planKey === 'solo_core') {
        return 'Subscribe to Solo Core'; // Allow trial users to subscribe
      }
      if (planKey === 'solo_pro') {
        return 'Upgrade to Solo Pro';
      }
      return 'Subscribe Now';
    }

    // For paid users, only show current plan as disabled
    if (isAuthenticated && userPlan && !userPlan.isTrialActive && userPlan.planType.replace('_', '-') === planKey.replace('_', '-')) {
      return 'Current Plan';
    }

    if (shouldShowPayment) {
      return 'Subscribe Now';
    }

    return 'Go to Dashboard';
  };

  // FIXED: Button style logic for trial users
  const getButtonStyle = (planKey: string, isPopular: boolean = false) => {
    // FIXED: Only disable if user is on a PAID plan that matches (not trial)
    if (isAuthenticated && userPlan && !userPlan.isTrialActive && userPlan.planType.replace('_', '-') === planKey.replace('_', '-')) {
      return 'bg-gray-300 text-gray-500 cursor-not-allowed';
    }

    if (shouldShowPayment || (isAuthenticated && userPlan)) {
      return isPopular 
        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
        : 'bg-blue-600 hover:bg-blue-700 text-white';
    }
    return isPopular 
      ? 'bg-blue-600 hover:bg-blue-700 text-white'
      : 'bg-gray-900 hover:bg-gray-800 text-white';
  };

  // FIXED: Handle plan click logic for trial users
  const handlePlanClick = async (planKey: string, billingPeriod: 'monthly' | 'yearly' | 'one_time') => {
    if (planKey === 'team_pilot') {
      navigate('/contact?plan=team-pilot');
      return;
    }

    // FIXED: Only prevent clicking if user is on a PAID plan that matches (not trial)
    if (isAuthenticated && userPlan && !userPlan.isTrialActive && userPlan.planType.replace('_', '-') === planKey.replace('_', '-')) {
      return; // Do nothing if clicking current PAID plan
    }

    // If user is authenticated (trial or paid), go to checkout
    if (isAuthenticated && (shouldShowPayment || userPlan)) {
      await createCheckoutSession(planKey, billingPeriod);
      return;
    }

    // If not authenticated, go to registration
    navigate('/register', { 
      state: { 
        planType: planKey.replace('_', '-'),
        billingPeriod: billingPeriod === 'yearly' ? 'yearly' : 'monthly'
      } 
    });
  };

  // Enhanced trial expiration banner
  const TrialExpiredBanner = () => (
    <div className="mb-8 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-6 text-center">
      <div className="flex items-center justify-center mb-4">
        <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <h3 className="text-xl font-bold">
          {isTrialExpired ? 'Your Free Trial Has Expired' : 
           fromTrialLimit ? 'Trial Sessions Used Up' :
           'Trial Ending Soon'}
        </h3>
      </div>
      <p className="text-red-100 mb-4">
        {isTrialExpired ? 'Continue your PM coaching journey with a paid subscription. Your progress and session history will be preserved.' :
         fromTrialLimit ? 'You\'ve used all 20 free trial sessions! Subscribe to continue practicing with unlimited access.' :
         'Subscribe now to continue your PM coaching journey with unlimited access.'}
      </p>
      <div className="bg-white/20 rounded-lg p-4 max-w-2xl mx-auto">
        <p className="text-sm">
          <strong>What you keep:</strong> All your coaching session history, feedback reports, 
          and progress tracking. <strong>What you gain:</strong> Unlimited access to continue practicing.
        </p>
      </div>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div id={sectionId} className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading pricing information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry option
  if (!pricingData) {
    return (
      <div id={sectionId} className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              Failed to load pricing information
            </div>
            <button
              onClick={fetchPricingData}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const plans = [
    {
      key: 'solo_core',
      name: 'Solo Core',
      description: 'PMP students, hobby PMs',
      monthlyPrice: isYearly ? pricingData.plans.solo_core.yearly : pricingData.plans.solo_core.monthly,
      features: pricingData.features.solo_core,
      buttonText: getButtonText('solo_core'),
      buttonStyle: getButtonStyle('solo_core', false),
      badge: 'STARTER',
      badgeStyle: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white',
      cardStyle: 'border-2 border-gray-300 hover:border-blue-400 shadow-md hover:shadow-xl bg-gradient-to-br from-gray-50 to-white'
    },
    {
      key: 'solo_pro',
      name: 'Solo Pro',
      description: 'Mid-career PMs seeking promotion',
      monthlyPrice: isYearly ? pricingData.plans.solo_pro.yearly : pricingData.plans.solo_pro.monthly,
      features: pricingData.features.solo_pro,
      buttonText: getButtonText('solo_pro'),
      buttonStyle: getButtonStyle('solo_pro', true),
      badge: 'MOST POPULAR',
      badgeStyle: 'bg-blue-500 text-white',
      cardStyle: 'border-2 border-blue-500 shadow-xl transform scale-105',
      isPopular: true
    },
    {
      key: 'team_pilot',
      name: 'Team Pilot-10',
      description: 'Small PMOs or L&D leads',
      monthlyPrice: pricingData.plans.team_pilot,
      features: pricingData.features.team_pilot,
      buttonText: getButtonText('team_pilot'),
      buttonStyle: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white',
      badge: 'FOR TEAMS',
      badgeStyle: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
      cardStyle: 'border-2 border-purple-400 hover:border-purple-500 shadow-lg'
    }
  ];

  return (
    <div id={sectionId} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Show trial expired banner if applicable */}
        {shouldShowPayment && isAuthenticated && (isTrialExpired || fromExpiration || fromTrialLimit) && <TrialExpiredBanner />}
        
        {/* Enhanced Trial User Banner */}
        {isAuthenticated && userPlan && userPlan.isTrialActive && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center mb-4">
              <svg className="w-8 h-8 mr-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h3 className="text-2xl font-bold text-green-800">
                Choose Your Plan - {userPlan.trialDaysLeft} Days Left in Trial
              </h3>
            </div>
            <p className="text-green-700 mb-4 text-lg">
              You've been trying Solo Core! Now choose your subscription to continue coaching.
            </p>
            <div className="bg-white/60 rounded-lg p-4 max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-green-800">Your Trial Usage</div>
                  <div className="text-2xl font-bold text-green-600 mt-1">{userPlan.creditsUsed}/{userPlan.creditsTotal}</div>
                  <div className="text-green-700">sessions completed</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-blue-800">Continue with Solo Core</div>
                  <div className="text-2xl font-bold text-blue-600 mt-1">$19/month</div>
                  <div className="text-blue-700">same as your trial</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-purple-800">Upgrade to Solo Pro</div>
                  <div className="text-2xl font-bold text-purple-600 mt-1">$39/month</div>
                  <div className="text-purple-700">3x more sessions</div>
                </div>
              </div>
            </div>
            <div className="mt-4 text-sm text-green-700">
              <strong>Your progress will be preserved</strong> with either plan choice
            </div>
          </div>
        )}
        
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {shouldShowPayment ? 'Choose Your Plan' : 'Simple, Transparent Pricing'}
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            {shouldShowPayment 
              ? 'Subscribe to continue your PM coaching journey'
              : 'Start with a 14-day free trial. No credit card required.'
            }
          </p>
          
          {/* Fallback notice */}
          {usingFallback && (
            <div className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-yellow-100 text-yellow-800 text-sm">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Showing offline pricing • 
              <button 
                onClick={fetchPricingData} 
                className="ml-1 underline hover:no-underline"
              >
                Try to reload
              </button>
            </div>
          )}
          
          {/* Launch Discount Banner */}
          {hasDiscount() && !shouldShowPayment && (
            <div className="mt-6 inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">
                🎉 Launch Special: {getDiscountPercentage()}% OFF automatically applied!
              </span>
            </div>
          )}
        </div>

        {/* Billing Toggle */}
        {showBillingToggle && !shouldShowPayment && (
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  !isYearly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  isYearly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly <span className="text-green-600 text-xs">(Save 2 months)</span>
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div key={plan.key} className={`relative bg-white rounded-xl transition-all duration-300 ${plan.cardStyle}`}>
              {/* Badge */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className={`px-4 py-1 rounded-full text-xs font-medium ${plan.badgeStyle}`}>
                  {plan.badge}
                </span>
              </div>
              
              <div className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  {/* Show original price if discounted */}
                  {hasDiscount() && !shouldShowPayment && (
                    <div className="text-center mb-2">
                      <span className="text-lg text-gray-500 line-through">
                        {getOriginalPrice(plan.monthlyPrice)}
                      </span>
                      <span className="text-gray-600">
                        {plan.key === 'team_pilot' ? '/one-off' : isYearly ? '/year' : '/mo'}
                      </span>
                    </div>
                  )}
                  
                  {/* Current/Discounted Price */}
                  <div className="text-center">
                    <span className="text-4xl font-bold text-gray-900">
                      {getPrice(plan.monthlyPrice)}
                    </span>
                    <span className="text-gray-600">
                      {plan.key === 'team_pilot' ? '/one-off' : isYearly ? '/year' : '/mo'}
                    </span>
                  </div>
                  
                  {/* Discount Badge */}
                  {hasDiscount() && !shouldShowPayment && (
                    <div className="text-center mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                        </svg>
                        Save {getDiscountPercentage()}%
                      </span>
                    </div>
                  )}
                  
                  {/* Annual Savings */}
                  {(() => {
                    if (!isYearly || plan.key === 'team_pilot' || shouldShowPayment) return null;
                    
                    const savings = getSavings(plan.key as 'solo_core' | 'solo_pro');
                    if (savings <= 0) return null;
                    
                    return (
                      <div className="text-sm text-green-600 mt-2 text-center">
                        Save {formatPrice(savings)} per year
                      </div>
                    );
                  })()}
                </div>
                
                {/* UPDATED: Current Plan Indicator - only for PAID users */}
                {isAuthenticated && userPlan && !userPlan.isTrialActive && userPlan.planType.replace('_', '-') === plan.key.replace('_', '-') && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <span className="text-blue-800 font-medium text-sm">
                      ✓ Your Current Plan
                    </span>
                  </div>
                )}

                {/* NEW: Trial Status Indicator - only for trial users */}
                {isAuthenticated && userPlan && userPlan.isTrialActive && plan.key === 'solo_core' && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <span className="text-green-800 font-medium text-sm">
                      🔄 Currently Trialing - {userPlan.trialDaysLeft} days left
                    </span>
                  </div>
                )}
                
                {/* UPDATED: Button with proper disabled state for trial users */}
                <button 
                  onClick={() => handlePlanClick(plan.key, isYearly ? 'yearly' : 'monthly')}
                  disabled={isCreatingCheckout || (isAuthenticated && userPlan && !userPlan.isTrialActive && userPlan.planType.replace('_', '-') === plan.key.replace('_', '-'))}
                  className={`w-full py-3 px-6 rounded-lg transition-all duration-200 mb-6 ${getButtonStyle(plan.key, plan.isPopular)} ${
                    isCreatingCheckout ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isCreatingCheckout ? 'Setting up...' : getButtonText(plan.key)}
                </button>
                
                <div className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gray-50 rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Need more than 10 seats?
            </h3>
            <p className="text-gray-600 mb-6">
              Get custom Enterprise pricing with volume discounts, SSO integration, 
              unlimited coaching sessions, and dedicated support.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition duration-150"
            >
              Contact Sales for Enterprise
            </Link>
          </div>
        </div>

        {/* FAQ Section */}
        {showFullFAQ && (
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Frequently Asked Questions
            </h3>
            <div className="max-w-3xl mx-auto">
              <div className="space-y-6">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">
                      {item.question}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          {shouldShowPayment ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
              <h3 className="font-semibold text-blue-900 mb-2">Secure Payment Processing</h3>
              <p className="text-blue-700 text-sm">
                All payments are processed securely through Stripe. You can cancel anytime and 
                your data will be preserved for 30 days in case you want to reactivate.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-500">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Secure payments</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingContent;